export type CatalogPrice = 'all' | 'free' | 'paid';

export type CatalogSort = 'featured' | 'newest' | 'price-asc' | 'price-desc' | 'az' | 'za';

export interface CatalogFilterItem {
  id: string;
  name: string;
  category: string;
  description: string;
  tags?: string[];
  source?: 'static' | 'published';
  distribution_mode?: 'free' | 'paid';
  adapter_ids?: string[];
  slug?: string;
  family?: string;
  price_minor?: number;
  published_at?: string;
}

export const CATALOG_PAGE_SIZE = 50;

function sortTitle(a: CatalogFilterItem, b: CatalogFilterItem) {
  return a.name.localeCompare(b.name);
}

function publishedTimestamp(item: CatalogFilterItem) {
  if (!item.published_at) return 0;
  const timestamp = Date.parse(item.published_at);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function catalogPrice(item: CatalogFilterItem) {
  if (item.source === 'static') return 0;
  if (Number.isSafeInteger(item.price_minor) && item.price_minor! >= 0) return item.price_minor!;
  return Number.POSITIVE_INFINITY;
}

export function filterAndSortCatalog<T extends CatalogFilterItem>(
  items: readonly T[],
  options: {
    query?: string;
    price?: CatalogPrice;
    adapter?: string;
    category?: string;
    onlySaved?: boolean;
    savedIds?: readonly string[];
    sort?: CatalogSort;
  } = {},
): T[] {
  const query = (options.query ?? '').trim().toLowerCase();
  const category = options.category ?? 'All';
  const price = options.price ?? 'all';
  const adapter = options.adapter ?? 'all';
  const saved = new Set(options.savedIds ?? []);

  const result = items.filter((item) => {
    const categoryMatches = category === 'All' || item.category === category || item.tags?.includes(category);
    const savedMatches = !options.onlySaved || saved.has(item.id);
    const mode = item.distribution_mode ?? (item.source === 'static' ? 'free' : undefined);
    const priceMatches = price === 'all' || mode === price;
    const adapterMatches = adapter === 'all' || item.adapter_ids?.includes(adapter);
    const searchable = [item.name, item.id, item.category, item.slug, item.family, item.description, ...(item.tags ?? [])].join(' ').toLowerCase();
    return priceMatches && adapterMatches && categoryMatches && savedMatches && (!query || searchable.includes(query));
  });

  if (options.sort === 'newest') {
    return [...result].sort((a, b) => publishedTimestamp(b) - publishedTimestamp(a) || sortTitle(a, b));
  }
  if (options.sort === 'price-asc') {
    return [...result].sort((a, b) => catalogPrice(a) - catalogPrice(b) || sortTitle(a, b));
  }
  if (options.sort === 'price-desc') {
    return [...result].sort((a, b) => catalogPrice(b) - catalogPrice(a) || sortTitle(a, b));
  }
  if (options.sort === 'az') return [...result].sort(sortTitle);
  if (options.sort === 'za') return [...result].sort((a, b) => sortTitle(b, a));
  return result;
}

export function paginateCatalog<T>(items: readonly T[], requestedPage: number, pageSize = CATALOG_PAGE_SIZE) {
  if (!Number.isInteger(pageSize) || pageSize < 1) throw new Error('pageSize must be a positive integer');
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const page = Math.min(Math.max(1, Number.isFinite(requestedPage) ? Math.trunc(requestedPage) : 1), totalPages);
  const offset = (page - 1) * pageSize;
  const visibleItems = items.slice(offset, offset + pageSize);

  return {
    page,
    totalPages,
    visibleItems,
    start: items.length ? offset + 1 : 0,
    end: offset + visibleItems.length,
  };
}
